import {NamedValue} from "../types/NamedValue";
import {DateOnly} from "../utils/DateOnly";
import {DateTime} from "../utils/DateTime";
import {DayOfWeek} from "../utils/DayOfWeek";
import {TimeOnly} from "../utils/TimeOnly";
import {TimeSpan} from "../utils/TimeSpan";
import {CurrencyOptions, LocaleOptions} from "./LocaleOptions.js";

type IntlDateTimeFormatWeekday = "long" | "short" | "narrow" | undefined;
type IntlDateTimeFormatMonth = "numeric" | "2-digit" | "long" | "short" | "narrow" | undefined;
type IntlSegmenterGranularity = "grapheme" | "word" | "sentence" | undefined;
type IntlCollatorUsage = "sort" | "search" | undefined;

type DefaultFormatTypes = "default" | "full" | "long" | "medium" | "short";

export type LocaleId = string & { readonly __brand: unique symbol };
export type DateFormat = DefaultFormatTypes | "day_month" | "month_year" | "smonth_year";
export type DatetimeFormat = DefaultFormatTypes;
export type TimeFormat = DefaultFormatTypes;
export type DurationFormat = DefaultFormatTypes;
export type NumberFormat = "default" | "decimal" | "integer" | "percent" | "currency";

export interface WeekInfo {
	firstDay: number;
	weekend: number[];
	minimalDays: number;
}

const defaultWeekInfo: WeekInfo = {
	firstDay: 7,
	weekend: [6, 7],
	minimalDays: 4
};

function getDayNamesInternal(locale: string, day: IntlDateTimeFormatWeekday): NamedValue<DayOfWeek>[] {
	const fmt: Intl.DateTimeFormat = new Intl.DateTimeFormat(locale, {
		weekday: day
	});
	//1º of october/2017 => 1=sunday, 2=monday, etc...
	const d: Date = new Date(2017, 9, 1);
	const names: NamedValue<DayOfWeek>[] = [];
	for (let i = 1; i < 8; ++i) {
		d.setDate(i);
		names.push({label: fmt.format(d), value: i - 1});
	}
	return names;
}

function getMonthNamesInternal(locale: string, month: IntlDateTimeFormatMonth): string[] {
	const fmt: Intl.DateTimeFormat = new Intl.DateTimeFormat(locale, {
		month
	});
	//day pinned to 1: with today's day-of-month (29-31), setMonth(1) would roll February into March
	const d: Date = new Date(2017, 0, 1);
	const monthNames = [];
	for (let i = 0; i < 12; ++i) {
		d.setMonth(i);
		monthNames.push(fmt.format(d));
	}
	return monthNames;
}

function filterSupportedLocales(locales: string[], intlRef: { supportedLocalesOf(locales: string[]): string[] } | undefined): string[] {
	return typeof intlRef?.supportedLocalesOf === "function" ? intlRef.supportedLocalesOf(locales) : locales;
}

function supportedLocalesOf(requestedLocales: string[]): string[] {
	let supported = filterSupportedLocales(requestedLocales, Intl.DateTimeFormat);
	supported = filterSupportedLocales(supported, Intl.NumberFormat);
	supported = filterSupportedLocales(supported, Intl.DurationFormat);
	supported = filterSupportedLocales(supported, Intl.PluralRules);
	supported = filterSupportedLocales(supported, Intl.ListFormat);
	return supported;
}

function getSeparator(value: string): string {
	return value.replace(/[01]/g, "");
}

function toValidDate(value: unknown): Date | null {
	const date = value instanceof Date ? value : new Date(value as any);
	return Number.isNaN(date.getTime()) ? null : date;
}

const numberProperties: string[] = ["style", "currency", "currencyDisplay", "useGrouping", "minimumIntegerDigits", "minimumFractionDigits", "maximumFractionDigits", "minimumSignificantDigits", "maximumSignificantDigits"];
const dateTimeProperties: string[] = ["weekday", "era", "year", "month", "day", "hour", "minute", "second", "timeZoneName", "formatMatcher", "hour12", "timeZone"];

function isNumberFormatOptions(format: any): format is Intl.NumberFormatOptions {
	return Object.keys(format).some(key => numberProperties.includes(key));
}

function isDateTimeFormatOptions(format: any): format is Intl.DateTimeFormatOptions {
	return Object.keys(format).some(key => dateTimeProperties.includes(key));
}

export default class Locale {
	private static _current: Locale | undefined;
	private static _invariant: Locale | undefined;

	private _cardinalPluralRules: Intl.PluralRules;
	private _collators: Map<IntlCollatorUsage, Intl.Collator> | undefined;
	private readonly _currency: CurrencyOptions;
	private readonly _customFormats: Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.ListFormat | Intl.DurationFormat>;
	private readonly _dateTimes: Map<DatetimeFormat, Intl.DateTimeFormat>;
	private readonly _dates: Map<DateFormat, Intl.DateTimeFormat>;
	private readonly _id: LocaleId;
	private readonly _lists: Map<string, Intl.ListFormat>;
	private readonly _locale: Intl.Locale;
	private _longDayNames: NamedValue<DayOfWeek>[] | undefined;
	private _longMonthNames: string[] | undefined;
	private readonly _numbers: Map<NumberFormat, Intl.NumberFormat>;
	private readonly _ordinalPluralRules: Intl.PluralRules;
	private readonly _segmenters: Map<IntlSegmenterGranularity, Intl.Segmenter>;
	private _shortDayNames: NamedValue<DayOfWeek>[] | undefined;
	private _shortMonthNames: string[] | undefined;
	private readonly _times: Map<TimeFormat, Intl.DateTimeFormat>;
	private readonly _durations: Map<DurationFormat, Intl.DurationFormat>;

	numberDecimalSeparator: string;
	numberGroupSeparator: string;

	private constructor(localeId: LocaleId, options?: LocaleOptions) {
		this._shortDayNames = undefined;
		this._longDayNames = undefined;
		this._shortMonthNames = undefined;
		this._longMonthNames = undefined;
		this._collators = undefined;
		this._currency = {
			currency: options?.currency?.currency || "XDR",
			display: options?.currency?.display || "symbol"
		};
		this._customFormats = new Map();
		this._lists = new Map();
		this._segmenters = new Map();
		this._id = supportedLocalesOf([localeId])[0] as LocaleId;
		this._ordinalPluralRules = new Intl.PluralRules(this._id, {
			type: "ordinal"
		});
		this._cardinalPluralRules = new Intl.PluralRules(this._id, {
			type: "cardinal"
		});
		this._dateTimes = new Map();
		this._dates = new Map();
		this._times = new Map();
		this._numbers = new Map();
		this._durations = new Map();
		let nf: Intl.NumberFormat = new Intl.NumberFormat(this._id, {
			style: "decimal",
			useGrouping: true
		});
		this.numberDecimalSeparator = getSeparator(nf.format(11.11));
		this.numberGroupSeparator = getSeparator(nf.format(111111));
		this._initializeTables(options?.twoDigitParts === true);
		this._locale = new Intl.Locale(this._id);
	}

	get localeId(): LocaleId {
		return this._id;
	}

	get locale(): Intl.Locale {
		return this._locale;
	}

	get longDayNames(): NamedValue<DayOfWeek>[] {
		return this._longDayNames ??= getDayNamesInternal(this._id, "long");
	}

	get longMonthNames(): string[] {
		return this._longMonthNames ??= getMonthNamesInternal(this._id, "long");
	}

	get shortDayNames(): NamedValue<DayOfWeek>[] {
		return this._shortDayNames ??= getDayNamesInternal(this._id, "short");
	}

	get shortMonthNames(): string[] {
		return this._shortMonthNames ??= getMonthNamesInternal(this._id, "short");
	}

	addCustomFormat(name: string, format: Intl.DateTimeFormatOptions): void;
	addCustomFormat(name: string, format: Intl.NumberFormatOptions): void;
	addCustomFormat(name: string, format: Intl.NumberFormatOptions | Intl.DateTimeFormatOptions): void {
		if (isNumberFormatOptions(format)) {
			this._customFormats.set(name, new Intl.NumberFormat(this._id, format));
			return;
		}

		if (isDateTimeFormatOptions(format)) {
			this._customFormats.set(name, new Intl.DateTimeFormat(this._id, format));
			return;
		}

		throw new Error("Unable to determine or ambiguous format type");
	}

	/**
	 * Joins a list of strings into a single grammatical enumeration using `Intl.ListFormat`.
	 * Formatters are cached per `type`+`style` combination, so repeated calls in a hot render path do
	 * not re-instantiate the (expensive) Intl object.
	 *
	 * The `type`/`style` pair mirrors the style token of a MessageFormat `{value, list, <type>-<style>}`
	 * placeholder (e.g. `conjunction-long`). Alternatively pass the name of a custom `Intl.ListFormat`
	 * registered via {@link addCustomFormat}.
	 *
	 * Examples for `en-US` with `["a", "b", "c"]`:
	 * | type / style                        | example output       |
	 * |-------------------------------------|----------------------|
	 * | `conjunction` / `long` (default)    | `a, b, and c`        |
	 * | `disjunction` / `long`              | `a, b, or c`         |
	 * | `unit` / `short`                    | `a, b, c`            |
	 * | `conjunction` / `narrow`            | `a, b, c`            |
	 *
	 * @param values The items to join. Non-string items must be converted by the caller (Intl throws on them).
	 * @param typeOrFormat A list `type`, or the name of a custom registered list format.
	 * @param style The list `style`; when provided, `typeOrFormat` is treated as the list `type`.
	 * @returns The localized, joined list.
	 */
	formatCollection(values: string[], type?: Intl.ListFormatType, style?: Intl.ListFormatStyle): string;
	formatCollection(values: string[], format: string): string;
	formatCollection(values: string[], typeOrFormat?: Intl.ListFormatType | string, style?: Intl.ListFormatStyle): string {
		let formatter: Intl.ListFormat;
		if (style != null) {
			const key = `${typeOrFormat ?? ""}-${style}`;
			formatter = this._lists.get(key) ?? this._lists.set(key, new Intl.ListFormat(this._id, {
				style: style,
				type: typeOrFormat as Intl.ListFormatType
			})).get(key)!;
		} else if (typeof typeOrFormat === "string") {
			formatter = this._customFormats.get(typeOrFormat) as Intl.ListFormat;
		} else {
			formatter = this._lists.get("") ?? this._lists.set("", new Intl.ListFormat(this._id)).get("")!;
		}

		return formatter.format(values);
	}

	/**
	 * Formats a date value using one of the built-in date styles (or a custom format registered via
	 * {@link addCustomFormat}). The `format` argument mirrors the style token of a MessageFormat
	 * `{value, date, <style>}` placeholder; MessageFormat sub-formats (skeletons/patterns) are not
	 * supported, and any unknown token falls back to the `"default"` style so a bad translation
	 * string never throws mid-render.
	 *
	 * Invalid dates (and raw strings that fail to parse) format to an empty string rather than throwing.
	 *
	 * Built-in styles (examples for `en-US`, reference date Tuesday, January 25, 2022):
	 * | format                | example output                    |
	 * |-----------------------|-----------------------------------|
	 * | `default` / `medium`  | `Jan 25, 2022`                    |
	 * | `short`               | `1/25/22`                         |
	 * | `long`                | `January 25, 2022`                |
	 * | `full`                | `Tuesday, January 25, 2022`       |
	 * | `day_month`           | `1/25`                            |
	 * | `smonth_year`         | `1/22`                            |
	 * | `month_year`          | `January 22`                      |
	 *
	 * @param value The date to format. A {@link DateOnly} is rendered from its UTC components.
	 * @param format A built-in {@link DateFormat} style, a custom format name, or omitted for `"default"`.
	 * @returns The localized date string, or `""` when the value is not a valid date.
	 */
	formatDate(value: Date | DateOnly, format?: DateFormat | string): string {
		const formatter: Intl.DateTimeFormat = this._dates.get((format ?? "default") as DateFormat) ?? this._customFormats.get(format as string) as Intl.DateTimeFormat ?? this._dates.get("default")!;
		if (value instanceof DateOnly) {
			return formatter.format(value.getUtcDate());
		}
		const date = toValidDate(value);
		return date == null ? "" : formatter.format(date);
	}

	/**
	 * Formats a date-and-time value using one of the built-in styles (or a custom format registered via
	 * {@link addCustomFormat}). The `format` argument mirrors the style token of a MessageFormat
	 * `{value, datetime, <style>}` placeholder; sub-formats (skeletons/patterns) are not supported, and any
	 * unknown token falls back to the `"default"` style rather than throwing.
	 *
	 * Invalid dates (and raw strings that fail to parse) format to an empty string rather than throwing.
	 *
	 * Built-in styles (examples for `en-US`, reference Tuesday, January 25, 2022, 11:00:00 PM GMT-3):
	 * | format               | example output                                              |
	 * |----------------------|------------------------------------------------------------|
	 * | `default` / `medium` | `Jan 25, 2022, 11:00:00 PM`                                |
	 * | `short`              | `1/25/22, 11:00 PM`                                        |
	 * | `long`               | `January 25, 2022 at 11:00:00 PM GMT-3`                    |
	 * | `full`               | `Tuesday, January 25, 2022 at 11:00:00 PM GMT-3`          |
	 *
	 * @param value The value to format. A {@link DateTime} is rendered from its UTC components.
	 * @param format A built-in {@link DatetimeFormat} style, a custom format name, or omitted for `"default"`.
	 * @returns The localized date-time string, or `""` when the value is not a valid date.
	 */
	formatDateTime(value: Date | DateTime, format?: DatetimeFormat | string): string {
		const formatter: Intl.DateTimeFormat = this._dateTimes.get((format ?? "default") as DatetimeFormat) ?? this._customFormats.get(format as string) as Intl.DateTimeFormat ?? this._dateTimes.get("default")!;
		if (value instanceof DateTime) {
			return formatter.format(value.getUtcDate());
		}

		const date = toValidDate(value);
		return date == null ? "" : formatter.format(date);
	}

	/**
	 * Formats a number using one of the built-in styles (or a custom format registered via
	 * {@link addCustomFormat}). The `format` argument mirrors the style token of a MessageFormat
	 * `{value, number, <style>}` placeholder; sub-formats (skeletons/patterns) are not supported, and any
	 * unknown token falls back to the `"default"` style rather than throwing.
	 *
	 * `currency` uses the currency and display configured on the culture (defaults to `XDR`/`symbol`).
	 *
	 * Built-in styles (examples for `en-US`):
	 * | format               | input     | example output   |
	 * |----------------------|-----------|------------------|
	 * | `default` / `decimal`| `1234.567`| `1,234.567`      |
	 * | `integer`            | `1234.567`| `1,235`          |
	 * | `percent`            | `0.85`    | `85%`            |
	 * | `currency` (USD)     | `1234.5`  | `$1,234.50`      |
	 *
	 * @param value The number to format.
	 * @param format A built-in {@link NumberFormat} style, a custom format name, or omitted for `"default"`.
	 * @returns The localized number string.
	 */
	formatNumber(value: number, format?: NumberFormat | string): string {
		const formatter: Intl.NumberFormat = this._numbers.get((format ?? "default") as NumberFormat) ?? this._customFormats.get(format as string) as Intl.NumberFormat ?? this._numbers.get("default")!;
		return formatter.format(value);
	}

	/**
	 * Formats a time value using one of the built-in styles (or a custom format registered via
	 * {@link addCustomFormat}). The `format` argument mirrors the style token of a MessageFormat
	 * `{value, time, <style>}` placeholder; sub-formats (skeletons/patterns) are not supported, and any
	 * unknown token falls back to the `"default"` style rather than throwing.
	 *
	 * Invalid times (and raw strings that fail to parse) format to an empty string rather than throwing.
	 *
	 * Built-in styles (examples for `en-US`, reference 11:00:00 PM GMT-3):
	 * | format                        | example output       |
	 * |-------------------------------|----------------------|
	 * | `default` / `medium` / `full` | `11:00:00 PM`        |
	 * | `short`                       | `11:00 PM`           |
	 * | `long`                        | `11:00:00 PM GMT-3`  |
	 *
	 * Note: `long` and `full` add the time-zone name; `default`, `medium` and `full` share the same
	 * hour/minute/second layout.
	 *
	 * @param value The time to format. A {@link TimeOnly} is rendered from its UTC components.
	 * @param format A built-in {@link TimeFormat} style, a custom format name, or omitted for `"default"`.
	 * @returns The localized time string, or `""` when the value is not a valid time.
	 */
	formatTime(value: Date | TimeOnly, format?: TimeFormat | string): string {
		const formatter: Intl.DateTimeFormat = this._times.get((format ?? "default") as TimeFormat) ?? this._customFormats.get(format as string) as Intl.DateTimeFormat ?? this._times.get("default")!;
		if (value instanceof TimeOnly) {
			return formatter.format(value.getUtcDate());
		}

		const date = toValidDate(value);
		return date == null ? "" : formatter.format(date);
	}

	/**
	 * Formats a {@link TimeSpan} duration using one of the built-in styles (or a custom format registered
	 * via {@link addCustomFormat}). The `format` argument mirrors the style token of a MessageFormat
	 * `{value, duration, <style>}` placeholder; sub-formats are not supported, and any unknown token falls
	 * back to the `"default"` style rather than throwing.
	 *
	 * Backed by `Intl.DurationFormat`. On runtimes lacking it, only a `digital`-like `h:m:s` fallback is
	 * available and every style resolves to it.
	 *
	 * Built-in styles (examples for `en-US`, reference 1 day 2 h 3 m 4 s 500 ms):
	 * | format                | underlying style | example output                                        |
	 * |-----------------------|------------------|-------------------------------------------------------|
	 * | `default` / `full`    | `digital`        | `1 day, 2:03:04.5`                                    |
	 * | `long`                | `long`           | `1 day, 2 hours, 3 minutes, 4 seconds, 500 milliseconds` |
	 * | `medium`              | `short`          | `1 day, 2 hr, 3 min, 4 sec, 500 ms`                  |
	 * | `short`               | `narrow`         | `1d 2h 3m 4s 500ms`                                  |
	 *
	 * @param value The duration to format.
	 * @param format A built-in {@link DurationFormat} style, a custom format name, or omitted for `"default"`.
	 * @returns The localized duration string.
	 */
	formatDuration(value: TimeSpan, format?: DurationFormat | string): string {
		const formatter: Intl.DurationFormat = this._durations.get((format ?? "default") as DurationFormat) ?? this._customFormats.get(format as string) as Intl.DurationFormat ?? this._durations.get("default")!;

		return formatter.format({
			days: value.getDays(),
			hours: value.getHours(),
			minutes: value.getMinutes(),
			seconds: value.getSeconds(),
			milliseconds: value.getMilliseconds()
		});
	}

	getCollator(usage: IntlCollatorUsage): Intl.Collator {
		const collators = this._collators ??= new Map();
		let collator = collators.get(usage);
		if (collator != null) {
			return collator;
		}
		collator = new Intl.Collator(this._id, {
			usage
		});
		collators.set(usage, collator);
		return collator;
	}

	getWeekInfo(): WeekInfo {
		try {
			const locale = this._locale;
			if ("getWeekInfo" in locale && typeof locale.getWeekInfo === "function") {
				return locale.getWeekInfo() as WeekInfo ?? defaultWeekInfo;
			} else if ("weekInfo" in locale && typeof locale.weekInfo === "object") {
				return locale.weekInfo as WeekInfo ?? defaultWeekInfo;
			}
		} catch {
		}

		return defaultWeekInfo;
	}

	segment(text: string, granularity: IntlSegmenterGranularity): Intl.Segments {
		let segmenter = this._segmenters.get(granularity);
		if (segmenter != null) {
			return segmenter.segment(text);
		}
		segmenter = new Intl.Segmenter(this._id, {
			granularity
		});
		this._segmenters.set(granularity, segmenter);
		return segmenter.segment(text);
	}

	selectPlural(value: number, isOrdinal: boolean): string {
		return isOrdinal ? this._ordinalPluralRules.select(value) : this._cardinalPluralRules.select(value);
	}

	/** Locale-aware lowercasing (e.g. Turkish dotless i) — use instead of String.toLowerCase for display text. */
	toLowerCase(value: string): string {
		return value.toLocaleLowerCase(this._id);
	}

	/** Locale-aware uppercasing — use instead of String.toUpperCase for display text. */
	toUpperCase(value: string): string {
		return value.toLocaleUpperCase(this._id);
	}

	private _initializeTables(twoDigitParts: boolean): void {
		const cultureName = this._id;
		//"numeric" keeps the locale's bare numbers; "2-digit" zero-pads them (years stay as-is: 4-digit needs no padding).
		const num: "numeric" | "2-digit" = twoDigitParts ? "2-digit" : "numeric";
		this._dateTimes.set("default", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: num,
			hour: num,
			minute: num,
			second: num
		}));
		this._dateTimes.set("short", new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: num,
			day: num,
			hour: num,
			minute: num
		}));
		this._dateTimes.set("medium", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: num,
			hour: num,
			minute: num,
			second: num
		}));
		this._dateTimes.set("long", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: num,
			hour: num,
			minute: num,
			second: num,
			timeZoneName: "short"
		}));
		this._dateTimes.set("full", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: num,
			hour: num,
			minute: num,
			second: num,
			timeZoneName: "short",
			weekday: "long"
		}));
		this._dates.set("default", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: num
		}));
		this._dates.set("short", new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: num,
			day: num
		}));
		this._dates.set("medium", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "short",
			day: num
		}));
		this._dates.set("long", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: num
		}));
		this._dates.set("full", new Intl.DateTimeFormat(cultureName, {
			year: "numeric",
			month: "long",
			day: num,
			weekday: "long"
		}));
		this._dates.set("day_month", new Intl.DateTimeFormat(cultureName, {
			month: num,
			day: num
		}));
		this._dates.set("smonth_year", new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: num
		}));
		this._dates.set("month_year", new Intl.DateTimeFormat(cultureName, {
			year: "2-digit",
			month: "long"
		}));

		this._times.set("default", new Intl.DateTimeFormat(cultureName, {
			hour: num,
			minute: num,
			second: num
		}));
		this._times.set("short", new Intl.DateTimeFormat(cultureName, {
			hour: num,
			minute: num
		}));
		this._times.set("medium", new Intl.DateTimeFormat(cultureName, {
			hour: num,
			minute: num,
			second: num
		}));
		this._times.set("long", new Intl.DateTimeFormat(cultureName, {
			hour: num,
			minute: num,
			second: num,
			timeZoneName: "short"
		}));
		this._times.set("full", new Intl.DateTimeFormat(cultureName, {
			hour: num,
			minute: num,
			second: num,
			timeZoneName: "short"
		}));

		this._numbers.set("default", new Intl.NumberFormat(cultureName, {
			style: "decimal"
		}));
		this._numbers.set("integer", new Intl.NumberFormat(cultureName, {
			maximumFractionDigits: 0,
			style: "decimal"
		}));
		this._numbers.set("decimal", new Intl.NumberFormat(cultureName, {
			style: "decimal"
		}));
		this._numbers.set("percent", new Intl.NumberFormat(cultureName, {
			style: "percent"
		}));
		this._numbers.set("currency", new Intl.NumberFormat(cultureName, {
			currency: this._currency.currency,
			currencyDisplay: this._currency.display,
			style: "currency"
		}));

		if (Intl.DurationFormat != null) {
			this._durations.set("default", new Intl.DurationFormat(cultureName, {
				style: "digital"
			}));
			this._durations.set("long", new Intl.DurationFormat(cultureName, {
				style: "long"
			}));
			this._durations.set("medium", new Intl.DurationFormat(cultureName, {
				style: "short"
			}));
			this._durations.set("short", new Intl.DurationFormat(cultureName, {
				style: "narrow"
			}));
			this._durations.set("full", new Intl.DurationFormat(cultureName, {
				style: "digital"
			}));
		} else {
			this._durations.set("default", {
				formatToParts(duration: Partial<Record<Intl.DurationFormatUnit, number>>): Intl.DurationFormatPart[] {
					return [];
				},
				resolvedOptions(): Intl.ResolvedDurationFormatOptions {
					return {
						days: "long",
						daysDisplay: "auto",
						fractionalDigits: 0,
						hours: "long",
						hoursDisplay: "auto",
						locale: "",
						microseconds: "long",
						microsecondsDisplay: "auto",
						milliseconds: "long",
						millisecondsDisplay: "auto",
						minutes: "long",
						minutesDisplay: "auto",
						months: "long",
						monthsDisplay: "auto",
						nanoseconds: "long",
						nanosecondsDisplay: "auto",
						numberingSystem: "",
						seconds: "long",
						secondsDisplay: "auto",
						style: "long",
						weeks: "long",
						weeksDisplay: "auto",
						years: "long",
						yearsDisplay: "auto"
					};
				},
				format: (value: {
					hours: number,
					minutes: number,
					seconds: number
				}) => `${value.hours}:${value.minutes}:${value.seconds}`
			});
		}
	}

	static getCurrent(): Locale {
		return this._current != null ? this._current : this.getInvariant();
	}

	static getInvariant(): Locale {
		return this._invariant ??= new Locale("en-US-POSIX" as LocaleId);
	}

	static setCurrent(localeId: LocaleId, options?: LocaleOptions): Locale {
		return this._current = new Locale(localeId, options);
	}
}
